import { BaseController, BaseState } from '../../../common/controllers/BaseController';
import { withRouter, WithRouterProps } from '../../../common/higher-order-components/with-router';
import { UrlHelper } from '../../../common/utils/UrlHelper';
import { PATHS } from '../../../routing/routes';
import { ReportOutletContextType } from '../../common/contexts/ReportOutletContext';
import { Report, ReportDone, ReportFailed, ReportPending } from '../../common/models/Report';
import { Box, Stack } from '@mui/material';
import { ReportDetails } from '../components/ReportDetails';
import { bind } from '../../../common/utils/decorators';
import { ReportRepository } from '../../common/services/ReportRepository';
import { ReportDiffRepository } from '../services/ReportDiffRepository';
import { ReportDiff } from '../models/ReportDiff';
import { ReportComparisonInfo } from '../components/ReportComparisonInfo';
import { ReportSummary, ReportSummaryRepository } from '../../summary/services/ReportSummaryRepository';
import { InventorySummaryCard } from '../components/InventorySummaryCard';
import { InvalidUrlError } from '../../../common/errors/errors';
import { KeyInsightsCard } from '../components/KeyInsightsCard';

const FETCH_INTERVAL_MS = 2500;

type Props = WithRouterProps<ReportOutletContextType> & {};

type State = BaseState & {
  initialLoad: boolean;
  loadingData: boolean;
  selectedReport: Report | null;
  reportDiff: ReportDiff | null;
  currentReportSummary: ReportSummary | null;
  selectedReportSummary: ReportSummary | null;
  isLoadingReportDiff: boolean;
  isVersionsListEmpty: boolean;
};

class Controller extends BaseController<Props, State> {
  private reportDiffFetchInterval: NodeJS.Timeout | null = null;
  private report: Report | null;
  private readonly reportRepository = new ReportRepository();
  private readonly reportDiffRepository = new ReportDiffRepository();
  private readonly reportSummaryRepository = new ReportSummaryRepository();

  constructor(props: Props, context: unknown) {
    super(props, context);

    this.report = null;

    this.state = {
      ...super.getBaseState(),
      initialLoad: true,
      loadingData: true,
      selectedReport: null,
      reportDiff: null,
      currentReportSummary: null,
      selectedReportSummary: null,
      isLoadingReportDiff: false,
      isVersionsListEmpty: false,
    };
  }

  componentDidMount(): void {
    super.componentDidMount();

    this.loadReportContext();
  }

  componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>): void {
    super.componentDidUpdate(prevProps, prevState);

    this.onReportContextChanged(prevProps.context.report, this.props.context.report);
  }

  componentWillUnmount() {
    super.componentWillUnmount();

    this.stopReportDiffFetch();
    this.reportSummaryRepository.cancelAllRequests();
    this.reportRepository.cancelAllRequests();
  }

  @bind
  async recreateReportDiff(selectedReportId: number) {
    this.setState({
      reportDiff: null,
    });
    this.startReportDiffFetch(selectedReportId, true);
  }

  @bind
  async fetchReportDiff(selectedReportId: number, recreateDiff: boolean) {
    const currentReportId = this.report?.getId();
    if (!currentReportId) {
      return;
    }

    try {
      let reportDiff = await this.reportDiffRepository.getReportDiff(currentReportId, selectedReportId);
      if (reportDiff === null || recreateDiff) {
        reportDiff = await this.reportDiffRepository.createReportDiff(currentReportId, selectedReportId);
      }
      this.setState({ reportDiff });
      if ((reportDiff?.isDone() || reportDiff?.isFailed()) && !recreateDiff) {
        this.stopReportDiffFetch();
        this.setState({ isLoadingReportDiff: false });
      }
    } catch (e: unknown) {
      this.setState({ isLoadingReportDiff: false });
      this.stopReportDiffFetch();
      this.handleError(e);
    }
  }

  stopReportDiffFetch() {
    this.reportDiffRepository.cancelAllRequests();
    if (this.reportDiffFetchInterval) {
      clearInterval(this.reportDiffFetchInterval);
      this.reportDiffFetchInterval = null;
    }
  }

  startReportDiffFetch(reportId: number, recreateDiff = false) {
    this.stopReportDiffFetch();
    this.reportDiffFetchInterval = setInterval(() => {
      this.fetchReportDiff(reportId, false);
    }, FETCH_INTERVAL_MS);
    this.fetchReportDiff(reportId, recreateDiff);
    this.setState({ isLoadingReportDiff: true });
  }

  @bind
  async fetchSelectedReport(reportId: number) {
    this.props.setSearchParams({ reportId: reportId.toString() }, { replace: true });

    this.setState({
      loadingData: true,
      reportDiff: null,
    });

    try {
      this.startReportDiffFetch(reportId);
      const [selectedReport, selectedReportSummary] = await Promise.all([
        this.reportRepository.getReport(reportId),
        this.reportSummaryRepository.getSummary(reportId),
      ]);
      this.setState({
        selectedReport,
        selectedReportSummary,
        loadingData: false,
      });
    } catch (e: unknown) {
      this.stopReportDiffFetch();
      this.setState({ loadingData: false, selectedReport: null, reportDiff: null, isLoadingReportDiff: false });
      this.handleError(e);
    }
  }

  getQueriedReportVersion(currentReport: Report) {
    const queryReportId = UrlHelper.getQueryParam('reportId', null);
    if (queryReportId === null) {
      return null;
    }

    const queriedReportVersion = currentReport
      .getVersions()
      .find((version) => version.getId() === parseInt(queryReportId) && version.getId() !== currentReport.getId());
    if (!queriedReportVersion) {
      throw new InvalidUrlError('Invalid URL: Could not find report version with queried ID');
    }

    return queriedReportVersion;
  }

  getSelectedReportVersion(currentReport: Report) {
    const indexOfCurrent = currentReport.getVersions().findIndex((version) => version.getId() === currentReport.getId());
    const previousVersion = currentReport.getVersions()[indexOfCurrent + 1];
    const queriedReportVersion = this.getQueriedReportVersion(currentReport);
    return queriedReportVersion ?? previousVersion;
  }

  @bind
  async initialReportFetch() {
    const { report } = this.props.context;

    if (!(report instanceof ReportDone)) return;

    try {
      this.report = report;
      const selectedReportVersion = this.getSelectedReportVersion(report);
      if (!selectedReportVersion) {
        this.setState({
          initialLoad: false,
          loadingData: false,
          isVersionsListEmpty: true,
        });
        return;
      }

      const currentReportSummaryPromise = this.reportSummaryRepository.getSummary(report.getId());
      const selectedReportSummaryPromise = this.reportSummaryRepository.getSummary(selectedReportVersion.getId());
      const selectedReportPromise = this.reportRepository.getReport(selectedReportVersion.getId());
      this.startReportDiffFetch(selectedReportVersion.getId());
      const [currentReportSummary, selectedReport, selectedReportSummary] = await Promise.all([
        currentReportSummaryPromise,
        selectedReportPromise,
        selectedReportSummaryPromise,
      ]);
      this.setState({
        initialLoad: false,
        loadingData: false,
        currentReportSummary,
        selectedReportSummary,
        selectedReport,
      });
    } catch (e: unknown) {
      this.setState({ initialLoad: false, loadingData: false });
      this.handleError(e);
      return null;
    }
  }

  render() {
    const { reportDiff, isLoadingReportDiff, initialLoad, currentReportSummary, selectedReportSummary, loadingData, selectedReport, isVersionsListEmpty } =
      this.state;

    const isFailedOrNotLoading =
      (!isLoadingReportDiff && !loadingData && !initialLoad && reportDiff === null) || (reportDiff !== null && reportDiff.isFailed());
    return this.renderBase(
      <>
        <Stack direction="row" gap={2} flexWrap="wrap">
          <Box flex={1} sx={{ minWidth: '30rem' }}>
            <ReportDetails
              isStaticInfo
              report={this.report}
              initialLoad={initialLoad}
              handleVersionChange={() => {}}
              profileLink={(r) => UrlHelper.generatePath(PATHS.PROFILE_DETAIL, { profileId: r.getProfile()?.getId()?.toString() || '' })}
            />
          </Box>
          {!isVersionsListEmpty && (
            <Box flex={1} sx={{ minWidth: '30rem' }}>
              <ReportDetails
                report={selectedReport}
                initialLoad={initialLoad}
                isLoading={loadingData || isLoadingReportDiff}
                handleVersionChange={this.fetchSelectedReport}
                profileLink={(r) => UrlHelper.generatePath(PATHS.PROFILE_DETAIL, { profileId: r.getProfile()?.getId()?.toString() || '' })}
              />
            </Box>
          )}
        </Stack>
        {!loadingData && this.report && (
          <ReportComparisonInfo
            currentReport={this.report}
            selectedReport={selectedReport}
            reportDiff={reportDiff}
            onRecreateComparisonClick={this.recreateReportDiff}
          />
        )}
        {!isFailedOrNotLoading && (
          <>
            <KeyInsightsCard reportDiff={reportDiff} isLoading={loadingData || isLoadingReportDiff} />
            <InventorySummaryCard
              currentReportSummary={currentReportSummary}
              selectedReportSummary={selectedReportSummary}
              isLoading={loadingData || isLoadingReportDiff}
            />
          </>
        )}
      </>
    );
  }

  private loadReportContext() {
    // When the component mounts, consider that the previous report is null
    this.onReportContextChanged(null, this.props.context.report);
  }

  private onReportContextChanged(prevReport: Report | null, currReport: Report | null) {
    // Report hasn't changed, do nothing
    if (prevReport === currReport) return;

    if (prevReport !== null && prevReport.getId() !== currReport?.getId()) {
      // Report has changed (e.g. through a version selector)
      // Reset the internal data and reload
      this.setState({ loadingData: true }, () => this.loadReportContext());
      return;
    }

    if (!currReport || currReport instanceof ReportPending) {
      // The report is not ready yet, display the initial loading state
      this.report = currReport;
      this.setState({ loadingData: true });
    } else if (currReport instanceof ReportFailed) {
      this.report = currReport;
      // The report is failed, display the blank loaded state
      this.setState({ initialLoad: false, loadingData: false });
    } else if (currReport instanceof ReportDone) {
      // The report is done, load the summary
      this.initialReportFetch();
    }
  }
}

export const ReportComparisonController = withRouter<ReportOutletContextType>(Controller);
